import type { Fraction } from '@mimir/common/util/fractionType';
import type { VideoApiActions } from '@mimir/client-common/util/apiActions';
import { StreamRoleId } from '@mimir/common/dto/mediaAvailability';
import { PlayableItemDto, PlayableItemGetOptions } from '@mimir/common/dto/playableItemDto';
import type { SegmentProvider, SegmentInfo } from '@mimir/cutter/frameProvider/demuxAndDecode/segmentedPacketProvider';
import { EncodedData } from '@mimir/common/mpegts/demuxerTypes';
import { addFractions, approximateFractionValue, divideFractions, multiplyFractions } from '@mimir/common/util/integerMath';
import { ItemSegmentUrlCache } from '@mimir/client-common/util/itemUrlCache';
import type { BasicFetch } from '@mimir/common/types/basicFetch';
import type { Logger } from '@mimir/common/types/logger';
import { readWholeStream } from '@mimir/common/util/readableStream';
import { demuxTsSegment } from '@mimir/common/mpegts/mpegTsDemuxer';

export function createMimirTsSegmentProvider(
  services: {
    apiActions: VideoApiActions,
    fetch: BasicFetch,
    logger: Logger,
  },
  itemId: string
): SegmentProvider {
  const { apiActions, fetch, logger } = services;

  const options : PlayableItemGetOptions = {
    useEditProxy: false,
    streamSetPreference: [
      [StreamRoleId.LiveRecordWebproxyTs],
      [StreamRoleId.LiveRecordAudioOnlyProxyTs],
      [StreamRoleId.LiveRecordAudioOnlyHighresTs],
      [StreamRoleId.LiveRecordHighresTs],
    ] ,
    peakOffset: undefined,
  };

  let item : PlayableItemDto | null = null;

  const itemSegmentCache =
    new ItemSegmentUrlCache(
      apiActions,
      itemId,
      'get',
      StreamRoleId.LiveRecordWebproxyTs,
      10,
    );

  return {
    async getSegmentInfoAt(position: Fraction): Promise<SegmentInfo | null> {
      if (item === null) {
        item = await apiActions.getPlayableItem(itemId, options);
        if (item === null) {
          throw Error('Failed to get playable item');
        }
      }

      if (item.media === null) {
        throw Error('Item has no media');
      }

      const segmentDuration = item.media.segmentDuration;

      const segmentIndex =
          Math.floor(approximateFractionValue(divideFractions(position, segmentDuration)));

      if ((segmentIndex < 0) || (segmentIndex >= item.media.segmentCount)) {
        return null;
      }

      const segmentIndexAsFraction = { numerator: segmentIndex, denominator: 1 };

      return {
        id: segmentIndex.toString(),
        start: multiplyFractions(segmentIndexAsFraction, segmentDuration),
        end: addFractions(multiplyFractions(segmentIndexAsFraction, segmentDuration), segmentDuration),
      };
    },
    async getSegment(id: string): Promise<EncodedData> {
      const segmentIndex = parseInt(id);
      if (segmentIndex.toString() !== id) {
        throw new Error(`Bad segment id: ${id}`);
      }
      const url = await itemSegmentCache.getMediaSegmentUrl(segmentIndex);

      logger.info(`MimirTsSegmentProvider: fetching segment: ${segmentIndex}`);

      const response = await fetch(url, { });

      if (!response.ok || (response.body === null)) {
        throw new Error(`Failed to fetch segment: ${response.status} ${response.statusText}`);
      }

      const body = response.body;
      return demuxTsSegment(await readWholeStream(body), false);
    },
    async getDuration(): Promise<Fraction> {
      const item = await apiActions.getPlayableItem(itemId, options);
      if (item === null) {
        throw Error('Failed to get playable item');
      }
      if (!item.playableDuration) {
        throw Error('Item does not have duration');
      }
      return item.playableDuration;
    },
  };
}
